import { DefaultScheduleDto, EmploymentStatusEnum, UserModel } from 'api/model';
import useForm, { BasicFormState, BasicFormValidators } from 'hooks/useForm';
import {
  convertToDateOfBirthString,
  formatDate,
  formatTime,
} from 'utils/date-helpers';

export enum UserFormField {
  name,
  employmentID,
  email,
  phoneNumber,
  emailConfirmed,
  roles,
  isActive,
  isAvailableForDriving,
  unavailableForDrivingUntil,
  unavailableForDrivingReason,
  autoApproveAssignment,
  autoFetchSpeedLimitRate,
  showInInvoicing,
  dateOfBirth,
  city,
  postalCode,
  countyId,
  locationSpec,
  canDriveManual,
  isFullTime,
  fullTimePercentage,
  certifiedToCallCustomers,
  canTestTrucks,
  canTestPremiumCars,
  canDoHomeDelivery,
  canDriveElectric,
  canDriveTrucks,
  canDriveTrailers,
  canDrivePremium,
  canDoLongDrives,
  canStayAtHotels,
  otherInfo,
  employmentStatusID,
  zipCodeLocalAreaID,
  businessUnitID,
  isAllowedToChangeAvailability,
  teamID,
  isTruckTransport,
  excludeFromInvoicing,
  excludeFromSalaryReport,
  greenPlate,
  isBetaTester,
  shouldSeeTimeReportSectionInApp,
  companyDepartmentID,
  isAllowedToOverrideAssigneeWarnings,
  canDriveCarTransportTrucks,
  supplierIds,
  isAllowedHalfdayPlanning,
  commentSignature,
  /**
   * Hemankarna i driftens ordning, kommaseparerade företags-id:n (samma form som supplierIds).
   * Ordningen är bara tie-break i ruttplaneringen — start och slut väljs som närmaste ankare.
   * Tar det tidigare enkelfältets plats, så att de efterföljande enumvärdena står kvar.
   */
  homeAnchorCompanyIDs,
  // Sist: enumvärdena används som nycklar, så ett nytt fält får inte flytta de befintliga.
  isRunner,
  /**
   * "Bara i nödfall" per behörighet (2026-10-11). Gäller ovanpå Can…-flaggan: behörig men
   * ruttplaneringen väljer helst någon annan. Personalkortet visar paren som Ja / I nödfall / Nej.
   */
  electricEmergencyOnly,
  premiumEmergencyOnly,
  testPremiumEmergencyOnly,
  manualEmergencyOnly,
  homeDeliveryEmergencyOnly,
  longDrivesEmergencyOnly,
  testTrucksEmergencyOnly,
  /**
   * Betald resa till hemankaret (TL-feedback 2026-10-13): företags-id för platsen resan betalas
   * från, t.ex. Borås C, eller '' när ingen resa betalas. Sätts av HR. Ruttplaneringen räknar då
   * resan som en kostnad för just den fälttestaren.
   */
  paidTravelFromCompanyID,
  /**
   * "Deltid, procent" för behovsanställda (2026-10-23): '25', '50', '75', '100' eller '' när den
   * inte är angiven. Bara information, visas i fälttestarväljaren. Delar plats med Heltidsprocent
   * i personalkortet: heltidare ser Heltidsprocent, behovsanställda den här.
   */
  partTimePercentage,
}

/** Valen i "Deltid, procent". Servern tar bara emot dessa (UserLogic.AllowedPartTimePercentages). */
export const partTimePercentageOptions = [25, 50, 75, 100];

/** Behörigheterna med tre nivåer: Can…-fältet och dess nödfallsflagga. */
export const tieredCapabilities: {
  can: UserFormField;
  emergencyOnly: UserFormField;
}[] = [
  {
    can: UserFormField.canDoHomeDelivery,
    emergencyOnly: UserFormField.homeDeliveryEmergencyOnly,
  },
  {
    can: UserFormField.canDriveElectric,
    emergencyOnly: UserFormField.electricEmergencyOnly,
  },
  {
    can: UserFormField.canDriveManual,
    emergencyOnly: UserFormField.manualEmergencyOnly,
  },
  {
    can: UserFormField.canDrivePremium,
    emergencyOnly: UserFormField.premiumEmergencyOnly,
  },
  {
    can: UserFormField.canTestPremiumCars,
    emergencyOnly: UserFormField.testPremiumEmergencyOnly,
  },
  {
    can: UserFormField.canTestTrucks,
    emergencyOnly: UserFormField.testTrucksEmergencyOnly,
  },
  {
    can: UserFormField.canDoLongDrives,
    emergencyOnly: UserFormField.longDrivesEmergencyOnly,
  },
];

/** Formulärets kommaseparerade hemankare → id:n i ordning. */
export const parseHomeAnchorIds = (value: string): number[] =>
  value
    .split(',')
    .filter((v) => v)
    .map((v) => Number(v));

const getInitialUserFormValues = (
  user?: UserModel
): BasicFormState<UserFormField> => {
  if (user) {
    return {
      [UserFormField.name]: user.name,
      [UserFormField.employmentID]: user.employmentID?.toString() ?? '',
      [UserFormField.email]: user.email,
      [UserFormField.phoneNumber]: user.phoneNumber,
      [UserFormField.roles]: user.roles.join(','),
      [UserFormField.isActive]: user.isActive ? 'true' : 'false',
      [UserFormField.isAvailableForDriving]: user.isAvailableForDriving
        ? 'true'
        : 'false',
      [UserFormField.unavailableForDrivingUntil]:
        user.unavailableForDrivingUntil
          ? formatDate(user.unavailableForDrivingUntil)
          : '',
      [UserFormField.unavailableForDrivingReason]:
        user.unavailableForDrivingReason ?? '',
      [UserFormField.emailConfirmed]: user.emailConfirmed ? 'true' : 'false',
      [UserFormField.autoApproveAssignment]: user.autoApproveAssignment
        ? 'true'
        : 'false',
      [UserFormField.showInInvoicing]: user.showInInvoicing ? 'true' : 'false',
      [UserFormField.dateOfBirth]: user.dateOfBirth
        ? convertToDateOfBirthString(formatDate(user.dateOfBirth))
        : '',
      [UserFormField.city]: user.city,
      [UserFormField.postalCode]: user.postalCode,
      [UserFormField.countyId]: user.countyId?.toString() ?? '',
      [UserFormField.locationSpec]: user.locationSpec,
      [UserFormField.isFullTime]: user.isFullTime ? 'true' : 'false',
      [UserFormField.fullTimePercentage]:
        user.fullTimePercentage?.toString() ?? '',
      [UserFormField.canDriveManual]: user.canDriveManual ? 'true' : 'false',
      [UserFormField.canDrivePremium]: user.canDrivePremium ? 'true' : 'false',
      [UserFormField.canDoLongDrives]: user.canDoLongDrives ? 'true' : 'false',
      [UserFormField.canTestTrucks]: user.canTestTrucks ? 'true' : 'false',
      [UserFormField.canTestPremiumCars]: user.canTestPremiumCars
        ? 'true'
        : 'false',
      [UserFormField.canDoHomeDelivery]: user.canDoHomeDelivery
        ? 'true'
        : 'false',
      [UserFormField.isRunner]: user.isRunner ? 'true' : 'false',
      [UserFormField.canDriveElectric]: user.canDriveElectric
        ? 'true'
        : 'false',
      [UserFormField.canDriveTrucks]: user.canDriveTrucks ? 'true' : 'false',
      [UserFormField.canDriveTrailers]: user.canDriveTrailers
        ? 'true'
        : 'false',
      [UserFormField.canStayAtHotels]: user.canStayAtHotels ? 'true' : 'false',
      [UserFormField.otherInfo]: user.otherInfo,
      [UserFormField.employmentStatusID]:
        user.employmentStatusID?.toString() ?? '0',
      [UserFormField.autoFetchSpeedLimitRate]:
        user.autoFetchSpeedLimitRate.toString() ?? '1.0',
      [UserFormField.zipCodeLocalAreaID]:
        user.zipCodeLocalArea?.id?.toString() ?? '',
      [UserFormField.businessUnitID]: user.businessUnitID?.toString() ?? '',
      [UserFormField.isAllowedToChangeAvailability]:
        user.isAllowedToChangeAvailability ? 'true' : 'false',
      [UserFormField.teamID]: user.teamID?.toString() ?? '',
      [UserFormField.certifiedToCallCustomers]: user.certifiedToCallCustomers
        ? 'true'
        : 'false',
      [UserFormField.isTruckTransport]: user.isTruckTransport
        ? 'true'
        : 'false',
      [UserFormField.excludeFromInvoicing]: user.excludeFromInvoicing
        ? 'true'
        : 'false',
      [UserFormField.excludeFromSalaryReport]: user.excludeFromSalaryReport
        ? 'true'
        : 'false',
      [UserFormField.greenPlate]: user.greenPlate ?? '',
      [UserFormField.isBetaTester]: user.isBetaTester ? 'true' : 'false',
      [UserFormField.shouldSeeTimeReportSectionInApp]:
        user.shouldSeeTimeReportSectionInApp ? 'true' : 'false',
      [UserFormField.companyDepartmentID]:
        user.companyDepartmentId?.toString() ?? '',
      // Listan fylls av personalvägarna. Det gamla enkelfältet är reserven för ett svar som
      // saknar listan — då är det listans första (och enda) ankare.
      [UserFormField.homeAnchorCompanyIDs]: (
        user.homeAnchorCompanyIDs ??
        (user.homeAnchorCompanyID != null ? [user.homeAnchorCompanyID] : [])
      ).join(','),
      [UserFormField.isAllowedToOverrideAssigneeWarnings]:
        user.isAllowedToOverrideAssigneeWarnings ? 'true' : 'false',
      [UserFormField.canDriveCarTransportTrucks]:
        user.canDriveCarTransportTrucks ? 'true' : 'false',
      [UserFormField.supplierIds]: user.supplierIds?.map((s) => s).join(','),
      [UserFormField.isAllowedHalfdayPlanning]: user.isAllowedHalfdayPlanning
        ? 'true'
        : 'false',
      [UserFormField.commentSignature]: user.commentSignature ?? '',
      [UserFormField.electricEmergencyOnly]: user.electricEmergencyOnly
        ? 'true'
        : 'false',
      [UserFormField.premiumEmergencyOnly]: user.premiumEmergencyOnly
        ? 'true'
        : 'false',
      [UserFormField.testPremiumEmergencyOnly]: user.testPremiumEmergencyOnly
        ? 'true'
        : 'false',
      [UserFormField.manualEmergencyOnly]: user.manualEmergencyOnly
        ? 'true'
        : 'false',
      [UserFormField.homeDeliveryEmergencyOnly]: user.homeDeliveryEmergencyOnly
        ? 'true'
        : 'false',
      [UserFormField.longDrivesEmergencyOnly]: user.longDrivesEmergencyOnly
        ? 'true'
        : 'false',
      [UserFormField.testTrucksEmergencyOnly]: user.testTrucksEmergencyOnly
        ? 'true'
        : 'false',
      [UserFormField.paidTravelFromCompanyID]:
        user.paidTravelFromCompanyID?.toString() ?? '',
      [UserFormField.partTimePercentage]:
        user.partTimePercentage?.toString() ?? '',
    };
  }

  return {
    [UserFormField.name]: '',
    [UserFormField.employmentID]: '',
    [UserFormField.email]: '',
    [UserFormField.phoneNumber]: '',
    [UserFormField.roles]: '',
    [UserFormField.isActive]: 'true',
    [UserFormField.isAvailableForDriving]: 'true',
    [UserFormField.unavailableForDrivingUntil]: '',
    [UserFormField.unavailableForDrivingReason]: '',
    [UserFormField.emailConfirmed]: 'true',
    [UserFormField.autoApproveAssignment]: 'true',
    [UserFormField.showInInvoicing]: 'false',
    [UserFormField.dateOfBirth]: '',
    [UserFormField.isFullTime]: 'false',
    [UserFormField.fullTimePercentage]: '',
    [UserFormField.autoFetchSpeedLimitRate]: '1.0',
    [UserFormField.city]: '',
    [UserFormField.postalCode]: '',
    [UserFormField.countyId]: '',
    [UserFormField.locationSpec]: '',
    [UserFormField.canDriveManual]: 'false',
    [UserFormField.canDrivePremium]: 'false',
    [UserFormField.canDoLongDrives]: 'false',
    [UserFormField.canTestTrucks]: 'false',
    [UserFormField.canTestPremiumCars]: 'false',
    [UserFormField.canDoHomeDelivery]: 'false',
    [UserFormField.isRunner]: 'false',
    [UserFormField.canDriveElectric]: 'false',
    [UserFormField.canDriveTrucks]: 'false',
    [UserFormField.canDriveTrailers]: 'false',
    [UserFormField.canStayAtHotels]: 'false',
    [UserFormField.otherInfo]: '',
    [UserFormField.employmentStatusID]: '0',
    [UserFormField.zipCodeLocalAreaID]: '',
    [UserFormField.businessUnitID]: '',
    [UserFormField.isAllowedToChangeAvailability]: 'false',
    [UserFormField.teamID]: '',
    [UserFormField.certifiedToCallCustomers]: 'false',
    [UserFormField.isTruckTransport]: 'false',
    [UserFormField.excludeFromInvoicing]: 'false',
    [UserFormField.excludeFromSalaryReport]: 'false',
    [UserFormField.greenPlate]: '',
    [UserFormField.isBetaTester]: 'false',
    [UserFormField.shouldSeeTimeReportSectionInApp]: 'false',
    [UserFormField.companyDepartmentID]: '',
    [UserFormField.homeAnchorCompanyIDs]: '',
    [UserFormField.isAllowedToOverrideAssigneeWarnings]: 'false',
    [UserFormField.canDriveCarTransportTrucks]: 'false',
    [UserFormField.supplierIds]: '',
    [UserFormField.isAllowedHalfdayPlanning]: 'false',
    [UserFormField.commentSignature]: '',
    [UserFormField.electricEmergencyOnly]: 'false',
    [UserFormField.premiumEmergencyOnly]: 'false',
    [UserFormField.testPremiumEmergencyOnly]: 'false',
    [UserFormField.manualEmergencyOnly]: 'false',
    [UserFormField.homeDeliveryEmergencyOnly]: 'false',
    [UserFormField.longDrivesEmergencyOnly]: 'false',
    [UserFormField.testTrucksEmergencyOnly]: 'false',
    [UserFormField.paidTravelFromCompanyID]: '',
    [UserFormField.partTimePercentage]: '',
  };
};

export const cleanWeekSchedule = (
  schedules: DefaultScheduleDto[]
): DefaultScheduleDto[] => {
  // Remove empty days and change 8:00 AM formatting to 08:00 on all days start and end strings
  // Also add isBaseSchedule property to first schedule
  const cleanedSchedules: DefaultScheduleDto[] = schedules.map(
    (schedule, index) => {
      return {
        ...schedule,
        isBaseSchedule: index === 0,
        schedule: schedule.schedule.map((dayDto) => {
          return {
            ...dayDto,
            start: dayDto.start || undefined,
            end: dayDto.end || undefined,
            startString: formatTime(dayDto.start)?.trim() || undefined,
            endString: formatTime(dayDto.end)?.trim() || undefined,
          };
        }),
      } as DefaultScheduleDto;
    }
  );

  return cleanedSchedules;
};

// converts YYYYMMDD or YYYY-MM-DD to Date
const parseYyyyMmDdToDate = (dateString: string): Date => {
  const digitsOnly = dateString.replace(/-/g, '');
  const year = digitsOnly.substring(0, 4);
  const month = digitsOnly.substring(4, 6);
  const day = digitsOnly.substring(6, 8);
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const emergencyOnly = (
  userForm: BasicFormState<UserFormField>,
  can: UserFormField,
  flag: UserFormField
) => userForm[can] === 'true' && userForm[flag] === 'true';

export const userFormToUserModel = (
  userForm: BasicFormState<UserFormField>,
  base?: UserModel
): UserModel => {
  return {
    userID: base?.userID ?? -1,
    name: userForm[UserFormField.name],
    employmentID: userForm[UserFormField.employmentID],
    email: userForm[UserFormField.email],
    phoneNumber: userForm[UserFormField.phoneNumber],
    roles: userForm[UserFormField.roles]
      .split(',')
      .filter((v) => v)
      .map((v) => Number(v)),
    isActive: userForm[UserFormField.isActive] === 'true',
    isAvailableForDriving:
      userForm[UserFormField.isAvailableForDriving] === 'true',
    unavailableForDrivingUntil:
      userForm[UserFormField.isAvailableForDriving] !== 'true' &&
      userForm[UserFormField.unavailableForDrivingUntil] !== ''
        ? parseYyyyMmDdToDate(
            userForm[UserFormField.unavailableForDrivingUntil]
          )
        : undefined,
    // Anledningen följer flaggan: skickas bara när personen är satt som ej bokningsbar.
    unavailableForDrivingReason:
      userForm[UserFormField.isAvailableForDriving] !== 'true' &&
      userForm[UserFormField.unavailableForDrivingReason].trim() !== ''
        ? userForm[UserFormField.unavailableForDrivingReason].trim()
        : undefined,
    emailConfirmed: userForm[UserFormField.emailConfirmed] === 'true',
    autoApproveAssignment:
      userForm[UserFormField.autoApproveAssignment] === 'true',
    showInInvoicing: userForm[UserFormField.showInInvoicing] === 'true',
    dateOfBirth:
      userForm[UserFormField.dateOfBirth] !== ''
        ? parseYyyyMmDdToDate(userForm[UserFormField.dateOfBirth])
        : undefined,
    city: userForm[UserFormField.city],
    postalCode: userForm[UserFormField.postalCode],
    countyId: Number(userForm[UserFormField.countyId]),
    locationSpec: userForm[UserFormField.locationSpec],
    isFullTime: userForm[UserFormField.isFullTime] === 'true',
    fullTimePercentage:
      userForm[UserFormField.fullTimePercentage] !== ''
        ? Number(userForm[UserFormField.fullTimePercentage])
        : undefined,
    canDriveManual: userForm[UserFormField.canDriveManual] === 'true',
    canDrivePremium: userForm[UserFormField.canDrivePremium] === 'true',
    canTestTrucks: userForm[UserFormField.canTestTrucks] === 'true',
    canTestPremiumCars: userForm[UserFormField.canTestPremiumCars] === 'true',
    canDoLongDrives: userForm[UserFormField.canDoLongDrives] === 'true',
    canDoHomeDelivery: userForm[UserFormField.canDoHomeDelivery] === 'true',
    isRunner: userForm[UserFormField.isRunner] === 'true',
    canDriveElectric: userForm[UserFormField.canDriveElectric] === 'true',
    canDriveTrucks: userForm[UserFormField.canDriveTrucks] === 'true',
    canDriveCarTransportTrucks:
      userForm[UserFormField.canDriveCarTransportTrucks] === 'true',
    canDriveTrailers: userForm[UserFormField.canDriveTrailers] === 'true',
    canStayAtHotels: userForm[UserFormField.canStayAtHotels] === 'true',
    otherInfo: userForm[UserFormField.otherInfo],
    employmentStatusID: Number(
      userForm[UserFormField.employmentStatusID]
    ) as EmploymentStatusEnum,
    autoFetchSpeedLimitRate: Number(
      userForm[UserFormField.autoFetchSpeedLimitRate]
    ),
    zipCodeLocalAreaID:
      userForm[UserFormField.zipCodeLocalAreaID] !== ''
        ? Number(userForm[UserFormField.zipCodeLocalAreaID])
        : undefined,

    businessUnitID:
      userForm[UserFormField.businessUnitID] !== ''
        ? Number(userForm[UserFormField.businessUnitID])
        : undefined,
    isAllowedToChangeAvailability:
      userForm[UserFormField.isAllowedToChangeAvailability] === 'true',
    teamID:
      userForm[UserFormField.teamID] !== ''
        ? Number(userForm[UserFormField.teamID])
        : undefined,
    certifiedToCallCustomers:
      userForm[UserFormField.certifiedToCallCustomers] === 'true',
    isTruckTransport: userForm[UserFormField.isTruckTransport] === 'true',
    excludeFromInvoicing:
      userForm[UserFormField.excludeFromInvoicing] === 'true',
    excludeFromSalaryReport:
      userForm[UserFormField.excludeFromSalaryReport] === 'true',
    greenPlate:
      userForm[UserFormField.greenPlate] !== ''
        ? userForm[UserFormField.greenPlate]
        : undefined,
    isBetaTester: userForm[UserFormField.isBetaTester] === 'true',
    shouldSeeTimeReportSectionInApp:
      userForm[UserFormField.shouldSeeTimeReportSectionInApp] === 'true',
    companyDepartmentId:
      userForm[UserFormField.companyDepartmentID] !== ''
        ? Number(userForm[UserFormField.companyDepartmentID])
        : undefined,
    // Alltid en lista, även tom: [] betyder "töm", medan ett utelämnat fält betyder "rör inte".
    homeAnchorCompanyIDs: parseHomeAnchorIds(
      userForm[UserFormField.homeAnchorCompanyIDs]
    ),
    isAllowedToOverrideAssigneeWarnings:
      userForm[UserFormField.isAllowedToOverrideAssigneeWarnings] === 'true',
    celebrateRating: base?.celebrateRating ?? false,
    supplierIds: userForm[UserFormField.supplierIds]
      .split(',')
      .filter((v) => v)
      .map((v) => Number(v)),
    isAllowedHalfdayPlanning:
      userForm[UserFormField.isAllowedHalfdayPlanning] === 'true',
    commentSignature:
      userForm[UserFormField.commentSignature] !== ''
        ? userForm[UserFormField.commentSignature]
        : undefined,
    // Nödfall betyder bara något när personen är behörig — "Nej" nollar båda.
    electricEmergencyOnly: emergencyOnly(
      userForm,
      UserFormField.canDriveElectric,
      UserFormField.electricEmergencyOnly
    ),
    premiumEmergencyOnly: emergencyOnly(
      userForm,
      UserFormField.canDrivePremium,
      UserFormField.premiumEmergencyOnly
    ),
    testPremiumEmergencyOnly: emergencyOnly(
      userForm,
      UserFormField.canTestPremiumCars,
      UserFormField.testPremiumEmergencyOnly
    ),
    manualEmergencyOnly: emergencyOnly(
      userForm,
      UserFormField.canDriveManual,
      UserFormField.manualEmergencyOnly
    ),
    homeDeliveryEmergencyOnly: emergencyOnly(
      userForm,
      UserFormField.canDoHomeDelivery,
      UserFormField.homeDeliveryEmergencyOnly
    ),
    longDrivesEmergencyOnly: emergencyOnly(
      userForm,
      UserFormField.canDoLongDrives,
      UserFormField.longDrivesEmergencyOnly
    ),
    testTrucksEmergencyOnly: emergencyOnly(
      userForm,
      UserFormField.canTestTrucks,
      UserFormField.testTrucksEmergencyOnly
    ),
    paidTravelFromCompanyID:
      userForm[UserFormField.paidTravelFromCompanyID] !== ''
        ? Number(userForm[UserFormField.paidTravelFromCompanyID])
        : undefined,
    partTimePercentage:
      userForm[UserFormField.partTimePercentage] !== ''
        ? Number(userForm[UserFormField.partTimePercentage])
        : undefined,
  };
};

export const UNAVAILABLE_REASON_REQUIRED =
  'Anledning måste anges när medarbetaren inte är tillgänglig för bokning';

const validators: BasicFormValidators<UserFormField> = {
  [UserFormField.unavailableForDrivingReason]: (
    value,
    _inputName,
    formState
  ) => {
    // Speglar serverkravet i UserLogic.UpdateUser: anledning krävs så länge flaggan är av.
    if (
      formState[UserFormField.isAvailableForDriving] !== 'true' &&
      value.trim() === ''
    ) {
      return { error: UNAVAILABLE_REASON_REQUIRED };
    }
    return undefined;
  },
  [UserFormField.fullTimePercentage]: (value, _inputName, formState) => {
    // Warn if percentage is set but isFullTime is not checked
    if (formState[UserFormField.isFullTime] !== 'true' && value !== '') {
      return {
        warning:
          'Heltidsprocent bör inte sättas när "Heltidsanställd" inte är ikryssad',
      };
    }
    return undefined;
  },
};

const useUserForm = (user?: UserModel) => {
  return useForm<UserFormField>(getInitialUserFormValues(user), validators);
};

export default useUserForm;
